import { DeviceManagementConfigurationSetting } from '@microsoft/microsoft-graph-types-beta';
type PartialPolicySettingNames =
    | ConfigurationPolicySettingNames.edgePasswordManagerEnabled
    | ConfigurationPolicySettingNames.edgeSyncDisabled
    | ConfigurationPolicySettingNames.chromePasswordManagerEnabled;

export interface DeviceConfigurationWindowsSettingSchema<T = PartialPolicySettingNames> {
    type: 'array';
    items: {
        type: 'object';
        properties: {
            settingInstance: {
                type: 'object';
                oneOf: {
                    properties: {
                        '@odata.type': {
                            type: 'string';
                            const: '#microsoft.graph.deviceManagementConfigurationChoiceSettingInstance';
                        };

                        settingDefinitionId: {
                            type: 'string';
                            const: T;
                        };

                        choiceSettingValue: {
                            type: 'object';
                            properties: {
                                value: {
                                    type: 'string';
                                    const: string;
                                };
                            };
                        };
                    };
                }[];
            };
        };
    };
    maxItems: number;
    minItems: number;
}

export enum ConfigurationPolicySettingNames {
    edgeSyncDisabled = 'device_vendor_msft_policy_config_microsoft_edge~policy~microsoft_edge_syncdisabled',
    edgePasswordManagerEnabled = 'device_vendor_msft_policy_config_microsoft_edge~policy~microsoft_edge~passwordmanager_passwordmanagerenabled',
    chromePasswordManagerEnabled = 'device_vendor_msft_policy_config_chromeintunev1~policy~googlechrome~passwordmanager_passwordmanagerenabled',
}

export const getConfigurationPolicyItemSettingValueFromSchema = (
    settingsSchema: DeviceConfigurationWindowsSettingSchema,
    settingName: ConfigurationPolicySettingNames,
): any => {
    const settingItem = settingsSchema.items.properties.settingInstance.oneOf.find((item) =>
        item.properties.settingDefinitionId.const.includes(settingName),
    );
    const propId = settingItem.properties.settingDefinitionId.const;
    const propValue = settingItem.properties.choiceSettingValue.properties.value.const;

    if (!propId || !propValue) return null;
    return settingToFormValueMap[settingName]?.values?.[`${propValue}`] || null;
};

export const getPolicyItemSettingValueFromData = (
    settings: DeviceManagementConfigurationSetting[],
    settingName: ConfigurationPolicySettingNames,
): 'enabled' | 'disabled' => {
    const settingItem = settings.find((item) => item.settingInstance?.settingDefinitionId?.includes(settingName));

    if (!settingItem) return null;

    const propId = settingItem.settingInstance?.settingDefinitionId;
    const propValue = settingItem.settingInstance?.['choiceSettingValue']?.value;
    if (!propId || !propValue) return null;
    return settingToFormValueMap[settingName]?.values?.[`${propValue}`] || null;
};

const settingToFormValueMap: Record<
    ConfigurationPolicySettingNames,
    { values: Record<string, 'enabled' | 'disabled'> }
> = {
    [ConfigurationPolicySettingNames.edgeSyncDisabled]: {
        values: {
            [ConfigurationPolicySettingNames.edgeSyncDisabled + '_1']: 'enabled',
            [ConfigurationPolicySettingNames.edgeSyncDisabled + '_0']: 'disabled',
        },
    },

    [ConfigurationPolicySettingNames.edgePasswordManagerEnabled]: {
        values: {
            [ConfigurationPolicySettingNames.edgePasswordManagerEnabled + '_1']: 'enabled',
            [ConfigurationPolicySettingNames.edgePasswordManagerEnabled + '_0']: 'disabled',
        },
    },

    [ConfigurationPolicySettingNames.chromePasswordManagerEnabled]: {
        values: {
            [ConfigurationPolicySettingNames.chromePasswordManagerEnabled + '_1']: 'enabled',
            [ConfigurationPolicySettingNames.chromePasswordManagerEnabled + '_0']: 'disabled',
        },
    },
};

// Combine these two functions

export function getConfigurationPolicySettingsForEdgeSchema(
    settingsForm: any,
): DeviceConfigurationWindowsSettingSchema<
    ConfigurationPolicySettingNames.edgePasswordManagerEnabled | ConfigurationPolicySettingNames.edgeSyncDisabled
> {
    const passwordManagerEnabled =
        settingsForm.passwordManagerEnabled === 'enabled'
            ? ConfigurationPolicySettingNames.edgePasswordManagerEnabled + '_1'
            : ConfigurationPolicySettingNames.edgePasswordManagerEnabled + '_0';

    const edgeSyncDisabled =
        settingsForm.edgeSyncDisabled === 'enabled'
            ? ConfigurationPolicySettingNames.edgeSyncDisabled + '_1'
            : ConfigurationPolicySettingNames.edgeSyncDisabled + '_0';

    return {
        type: 'array',
        items: {
            type: 'object',
            properties: {
                settingInstance: {
                    type: 'object',
                    oneOf: [
                        {
                            properties: {
                                '@odata.type': {
                                    type: 'string',
                                    const: '#microsoft.graph.deviceManagementConfigurationChoiceSettingInstance',
                                },
                                settingDefinitionId: {
                                    type: 'string',
                                    const: ConfigurationPolicySettingNames.edgeSyncDisabled,
                                },
                                choiceSettingValue: {
                                    type: 'object',
                                    properties: {
                                        value: { type: 'string', const: edgeSyncDisabled },
                                    },
                                },
                            },
                        },
                        {
                            properties: {
                                '@odata.type': {
                                    type: 'string',
                                    const: '#microsoft.graph.deviceManagementConfigurationChoiceSettingInstance',
                                },
                                settingDefinitionId: {
                                    type: 'string',
                                    const: ConfigurationPolicySettingNames.edgePasswordManagerEnabled,
                                },
                                choiceSettingValue: {
                                    type: 'object',
                                    properties: {
                                        value: { type: 'string', const: passwordManagerEnabled },
                                    },
                                },
                            },
                        },
                    ],
                },
            },
        },
        maxItems: 2,
        minItems: 2,
    };
}

export function getConfigurationPolicySettingsForChromeSchema(
    settingsForm: any,
): DeviceConfigurationWindowsSettingSchema<ConfigurationPolicySettingNames.chromePasswordManagerEnabled> {
    const passwordManagerEnabled =
        settingsForm.passwordManagerEnabled === 'enabled'
            ? ConfigurationPolicySettingNames.chromePasswordManagerEnabled + '_1'
            : ConfigurationPolicySettingNames.chromePasswordManagerEnabled + '_0';

    return {
        type: 'array',
        items: {
            type: 'object',
            properties: {
                settingInstance: {
                    type: 'object',
                    oneOf: [
                        {
                            properties: {
                                '@odata.type': {
                                    type: 'string',
                                    const: '#microsoft.graph.deviceManagementConfigurationChoiceSettingInstance',
                                },
                                settingDefinitionId: {
                                    type: 'string',
                                    const: ConfigurationPolicySettingNames.chromePasswordManagerEnabled,
                                },
                                choiceSettingValue: {
                                    type: 'object',
                                    properties: {
                                        value: { type: 'string', const: passwordManagerEnabled },
                                    },
                                },
                            },
                        },
                    ],
                },
            },
        },
        maxItems: 1,
        minItems: 1,
    };
}

export interface AssignmentSchema {
    type: string;
    minItems: number;
    maxItems: number;
    items?: {
        type: string;
        properties: {
            target: {
                type: string;
                oneOf: AssignmentItem[];
            };
        };
    };
}

interface AssignmentItem {
    properties: {
        groupId?: {
            const: string;
            type: string;
        };
        '@odata.type': {
            const: string;
            type: string;
        };
    };
}
